import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import PackageCard from "../components/PackageCard";
import ComingSoonPackageCard from "../components/ComingSoonPackageCard";
import { Package } from "../types";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import { useBookingModal } from "../context/BookingModalContext";
import rajasthanImage from "../assets/images/package-rajasthan.jpg";
import europeImage from "../assets/images/destination-europe.jpg";
import keralaImage from "../assets/images/destination-kerala.jpg";
import dubaiImage from "../assets/images/destination-dubai.jpg";
import manaliImage from "../assets/images/package-manali.jpg";
import singaporeImage from "../assets/images/package-singapore.jpg";
import goaImage from "../assets/images/package-goa.jpg";
import thailandImage from "../assets/images/package-thailand.jpg";
import chardhamImage from "../assets/images/package-chardham.jpg";
import baliImage from "../assets/images/package-bali.jpg";

export const allPackages: Package[] = [
  { id: "1", type: "domestic", price: 15000, days: 5, images: [rajasthanImage], commissionPercentage: 10 },
  { id: "2", type: "international", price: 120000, days: 10, images: [europeImage], commissionPercentage: 12 },
  { id: "3", type: "domestic", price: 22000, days: 6, images: [keralaImage], commissionPercentage: 11 },
  { id: "4", type: "international", price: 85000, days: 5, images: [dubaiImage], commissionPercentage: 13 },
  { id: "5", type: "domestic", price: 18000, days: 7, images: [manaliImage], commissionPercentage: 10 },
  { id: "6", type: "international", price: 75000, days: 6, images: [singaporeImage], commissionPercentage: 12 },
  { id: "7", type: "domestic", price: 12000, days: 4, images: [goaImage], commissionPercentage: 9 },
  { id: "8", type: "international", price: 55000, days: 8, images: [thailandImage], commissionPercentage: 11 },
  { id: "9", type: "domestic", tags: ["religious"], price: 28000, days: 9, images: [chardhamImage], commissionPercentage: 10 },
  { id: "10", type: "domestic", tags: ["honeymoon"], price: 26000, days: 6, images: [keralaImage], commissionPercentage: 12 },
  { id: "11", type: "domestic", tags: ["honeymoon"], price: 20000, days: 5, images: [goaImage], commissionPercentage: 10 },
  { id: "12", type: "international", tags: ["honeymoon"], price: 95000, days: 6, images: [baliImage], commissionPercentage: 13 },
];

type Filter = "all" | "domestic" | "international" | "honeymoon" | "religious";

const filterKeys: Record<Filter, string> = {
  all: "packages.filters.all",
  domestic: "packages.filters.domestic",
  international: "packages.filters.international",
  honeymoon: "packages.filters.honeymoon",
  religious: "packages.filters.religious",
};

const Packages: React.FC = () => {
  const { t } = useTranslation();
  const { open } = useBookingModal();
  const [filter, setFilter] = useState<Filter>("all");

  const filtered =
    filter === "all"
      ? allPackages
      : allPackages.filter(
          (p) => p.type === filter || p.tags?.includes(filter as "honeymoon" | "religious")
        );

  useDocumentMeta({
    title: t("packages.metaTitle"),
    description: t("packages.metaDescription"),
    path: "/packages",
  });

  return (
    <div className="bg-sand-50 min-h-screen">
      {/* Header */}
      <div className="relative bg-navy-950 py-24 text-white text-center overflow-hidden">
        <div className="absolute -top-20 left-10 w-80 h-80 rounded-full bg-brand-500/10 blur-3xl" />
        <div className="relative">
          <span className="inline-block border border-brand-400/40 bg-brand-500/10 text-brand-300 text-xs sm:text-sm font-semibold px-4 py-1.5 rounded-full mb-5 uppercase tracking-[0.2em]">
            {t("packages.eyebrow")}
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold mt-2">{t("packages.title")}</h1>
          <div className="w-16 h-[3px] bg-gradient-to-r from-brand-600 to-brand-400 mx-auto mt-6 rounded-full" />
          <p className="text-navy-200 mt-6 text-lg max-w-xl mx-auto px-6">
            {t("packages.subtitle")}
          </p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="max-w-7xl mx-auto px-6 pt-12 flex justify-center gap-3 flex-wrap">
        {(Object.keys(filterKeys) as Filter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            aria-pressed={filter === f}
            className={`px-6 py-2.5 rounded-full font-bold text-sm transition-all ${
              filter === f
                ? "bg-gradient-to-r from-brand-600 to-brand-400 text-navy-950 shadow-gold"
                : "border-2 border-navy-900/15 text-navy-900/60 hover:border-brand-500 hover:text-brand-700 bg-white"
            }`}
          >
            {t(filterKeys[f])}
          </button>
        ))}
      </div>

      {/* Package Grid */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {filtered.map((pkg) => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              title={t(`packages.items.${pkg.id}.title`)}
              description={t(`packages.items.${pkg.id}.description`)}
              onBook={open}
            />
          ))}
          <ComingSoonPackageCard
            onInquire={() => open({ title: t("packages.cta.customTitle") })}
          />
        </div>
      </div>

      {/* CTA */}
      <section className="py-20 px-6 bg-gradient-to-r from-brand-700 via-brand-600 to-brand-500">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-4 text-white">
            {t("packages.cta.title")}
          </h2>
          <p className="text-brand-100 mb-9 text-lg">
            {t("packages.cta.subtitle")}
          </p>
          <button
            onClick={() => open({ title: t("packages.cta.customTitle") })}
            className="bg-navy-950 text-brand-300 font-bold px-10 py-4 rounded-full hover:shadow-2xl hover:-translate-y-0.5 transition-all text-lg shadow-xl"
          >
            {t("packages.cta.button")}
          </button>
        </div>
      </section>
    </div>
  );
};

export default Packages;
